/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

function Billing() {
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");
  const [page] = React.useState(1);
  const [limit] = React.useState(10);
  const [rows, setRows] = React.useState([]);
  const [invoiceList, setInvoiceList] = React.useState([]);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = React.useState(null);

  const getAuthToken = React.useCallback(
    () =>
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "",
    []
  );

  const getApiBaseUrl = React.useCallback(
    () =>
      (
        process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
      ).replace(/\/+$/, ""),
    []
  );

  const handleInvoiceDownload = React.useCallback(
    async (invoiceId) => {
      if (!invoiceId || downloadingInvoiceId) return;

      setDownloadingInvoiceId(String(invoiceId));
      const token = getAuthToken();
      const apiBaseUrl = getApiBaseUrl();
      const downloadUrl = `${apiBaseUrl}/invoices/${encodeURIComponent(
        String(invoiceId)
      )}/download?format=pdf`;

      try {
        const response = await axios.get(downloadUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: "blob",
        });

        const blobUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `invoice-${invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (_error) {
        // Keep UI stable if download fails.
      } finally {
        setDownloadingInvoiceId(null);
      }
    },
    [downloadingInvoiceId, getApiBaseUrl, getAuthToken]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <MDTypography sx={{ color: "#000", fontSize: "0.9rem", fontWeight: 700 }}>
            INVOICE
          </MDTypography>
        ),
        accessor: "invoice",
        align: "left",
      },
      {
        Header: (
          <MDTypography sx={{ color: "#000", fontSize: "0.9rem", fontWeight: 700 }}>
            CUSTOMER
          </MDTypography>
        ),
        accessor: "customer",
        align: "left",
      },
      {
        Header: (
          <MDTypography sx={{ color: "#000", fontSize: "0.9rem", fontWeight: 700 }}>
            AMOUNT
          </MDTypography>
        ),
        accessor: "amount",
        align: "center",
      },
      {
        Header: (
          <MDTypography sx={{ color: "#000", fontSize: "0.9rem", fontWeight: 700 }}>
            STATUS
          </MDTypography>
        ),
        accessor: "status",
        align: "center",
      },
      {
        Header: (
          <MDTypography sx={{ color: "#000", fontSize: "0.9rem", fontWeight: 700 }}>
            DATE
          </MDTypography>
        ),
        accessor: "date",
        align: "center",
      },
      {
        Header: (
          <MDTypography sx={{ color: "#000", fontSize: "0.9rem", fontWeight: 700 }}>
            DOWNLOAD
          </MDTypography>
        ),
        accessor: "download",
        align: "center",
      },
    ],
    []
  );

  React.useEffect(() => {
    const token = getAuthToken();
    const apiBaseUrl = getApiBaseUrl();

    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}/invoices`);

    const params = { page, limit };
    if (search.trim()) params.search = search.trim();
    if (status.trim()) params.status = status.trim();
    if (from) params.dateFrom = from;
    if (to) params.dateTo = to;

    let isMounted = true;
    const fetchInvoices = async () => {
      for (const url of requestUrls) {
        try {
          const { data } = await axios.get(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            params,
          });
          if (!isMounted) return;
          const payload = data?.data || data || {};
          const list = payload.invoices || payload.docs || payload.items || payload.results || [];
          setInvoiceList(list);
          return;
        } catch (_error) {
          // Try next URL shape.
        }
      }
      if (isMounted) setInvoiceList([]);
    };

    fetchInvoices();
    return () => {
      isMounted = false;
    };
  }, [from, getApiBaseUrl, getAuthToken, limit, page, search, status, to]);

  React.useEffect(() => {
    const mappedRows = invoiceList.map((item) => {
      const amountValue = item.amount ?? item.totalAmount ?? item.total ?? 0;
      const invoiceDownloadId = item.id || item._id || item.invoiceId || item.invoiceNumber;
      const invoiceId =
        item.invoiceNumber || item.invoiceNo || item.invoiceId || item.id || item._id || "-";
      const customerName =
        item.customerName ||
        item.customer?.name ||
        item.user?.name ||
        item.name ||
        item.customer ||
        "-";
      const statusValue = String(item.status || item.paymentStatus || "-").toLowerCase();
      const rawDate =
        item.issuedAt || item.invoiceDate || item.createdAt || item.date || item.updatedAt;
      return {
        invoice: invoiceId,
        customer: customerName,
        amount:
          typeof amountValue === "number"
            ? `$${amountValue.toLocaleString()}`
            : String(amountValue || "-"),
        status: statusValue,
        date: rawDate
          ? new Date(rawDate).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-",
        download: (
          <IconButton
            size="small"
            aria-label="download invoice"
            disabled={!invoiceDownloadId || Boolean(downloadingInvoiceId)}
            onClick={() => handleInvoiceDownload(invoiceDownloadId)}
          >
            {String(downloadingInvoiceId) === String(invoiceDownloadId) ? (
              <CircularProgress size={18} sx={{ color: "rgba(229, 10, 70, 1)" }} />
            ) : (
              <Icon fontSize="small">download</Icon>
            )}
          </IconButton>
        ),
      };
    });
    setRows(mappedRows);
  }, [downloadingInvoiceId, handleInvoiceDownload, invoiceList]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/*
          Previous billing widgets hidden per requirement:
          - MasterCard / Info cards
          - PaymentMethod / Invoices
          - BillingInformation / Transactions
        */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="contained"
                borderRadius="lg"
                coloredShadow="none"
                sx={{ background: "rgba(229, 10, 70, 1)" }}
              >
                <MDTypography variant="h6" color="white">
                  Payments
                </MDTypography>
              </MDBox>
              <MDBox p={3} pb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Search (invoice/customer)"
                      fullWidth
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <MDInput
                      label="Status (paid/pending/failed)"
                      fullWidth
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MDInput
                      label="From"
                      type="date"
                      fullWidth
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MDInput
                      label="To"
                      type="date"
                      fullWidth
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={0}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Backdrop
        open={Boolean(downloadingInvoiceId)}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <MDBox display="flex" flexDirection="column" alignItems="center" gap={1.5}>
          <CircularProgress color="inherit" size={36} />
          <MDTypography variant="button" color="white" fontWeight="medium">
            Preparing PDF download...
          </MDTypography>
        </MDBox>
      </Backdrop>
    </DashboardLayout>
  );
}

export default Billing;
